import { useMemo, useState } from "react";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { groupRecipeMembers } from "@/lib/manufacturing/groupMembers";
import { updateManufacturingRecipeGroup } from "@/lib/manufacturing/recipeGroupsClient";
import type { MfgRecipeGroupSummary } from "@/lib/manufacturing/recipeGroupsClient";
import { previewRecipeMetaRename } from "@/lib/manufacturing/recipeMetaRenamePreview";
import { bulkUpdateManufacturingRecipesMeta } from "@/lib/manufacturing/recipesClient";
import { normalizeSeriesCode, seriesCodeFromName } from "@/lib/manufacturing/seriesWizardPlan";
import { defaultSeriesMemberRenameFinds } from "@/lib/manufacturing/seriesRenamePlan";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

/** Rename a recipe group and optionally retitle member product names/codes. */
export function ManufacturingModelsGroupRenamePanel({
  group,
  recipes,
  canEditCode,
  busy,
  setBusy,
  onError,
  onDone,
}: {
  group: MfgRecipeGroupSummary;
  recipes: readonly MfgRecipeSummary[];
  canEditCode: boolean;
  busy: boolean;
  setBusy: (v: boolean) => void;
  onError: (msg: string | null) => void;
  onDone: (message: string) => void;
}) {
  const members = useMemo(() => {
    const sections = groupRecipeMembers(recipes, group.id);
    return sections.flatMap((s) => s.recipes);
  }, [recipes, group.id]);

  const defaults = useMemo(
    () => defaultSeriesMemberRenameFinds(group, members),
    // Only seed when group identity changes — not on every member list refresh mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional seed-from-group
    [group.id, group.code, group.name],
  );

  const [open, setOpen] = useState(false);
  const [name, setName] = useState(group.name);
  const [code, setCode] = useState(group.code);
  const [retitleMembers, setRetitleMembers] = useState(true);
  const [codeFind, setCodeFind] = useState(defaults.codeFind);
  const [nameFind, setNameFind] = useState(defaults.nameFind);

  const outsideCodes = useMemo(() => {
    const memberIds = new Set(members.map((m) => m.id));
    return new Set(
      recipes
        .filter((r) => !memberIds.has(r.id))
        .map((r) => r.code.trim().toLowerCase())
        .filter(Boolean),
    );
  }, [recipes, members]);

  const nextCode = normalizeSeriesCode(code) || seriesCodeFromName(name);
  const nextName = name.trim();

  const codePreview = useMemo(() => {
    if (!retitleMembers || !canEditCode || !codeFind.trim() || !nextCode) return null;
    if (codeFind.trim().toLowerCase() === nextCode.toLowerCase()) return null;
    return previewRecipeMetaRename(
      members,
      { field: "code", find: codeFind, replace: nextCode, caseInsensitive: true },
      { existingCodesOutsideSelection: outsideCodes },
    );
  }, [retitleMembers, canEditCode, codeFind, nextCode, members, outsideCodes]);

  const namePreview = useMemo(() => {
    if (!retitleMembers || !nameFind.trim() || !nextName) return null;
    if (nameFind.trim().toLowerCase() === nextName.toLowerCase()) return null;
    return previewRecipeMetaRename(members, {
      field: "name",
      find: nameFind,
      replace: nextName,
      caseInsensitive: true,
    });
  }, [retitleMembers, nameFind, nextName, members]);

  const blockers = [
    ...(codePreview?.blockers ?? []),
    ...(namePreview?.blockers ?? []),
  ];
  const matchedCount =
    (codePreview?.matchedCount ?? 0) + (namePreview?.matchedCount ?? 0);

  function resetFromGroup() {
    setName(group.name);
    setCode(group.code);
    const d = defaultSeriesMemberRenameFinds(group, members);
    setCodeFind(d.codeFind);
    setNameFind(d.nameFind);
    setRetitleMembers(true);
  }

  async function applyRename() {
    if (!nextName) {
      onError("Series name is required");
      return;
    }
    if (!nextCode) {
      onError("Series code is required");
      return;
    }
    if (blockers.length > 0) {
      onError(blockers[0]?.error ?? "Rename has blockers");
      return;
    }

    setBusy(true);
    onError(null);

    const groupRes = await updateManufacturingRecipeGroup({
      recipe_group_id: group.id,
      name: nextName,
      code: nextCode,
    });
    if (!groupRes.ok) {
      setBusy(false);
      onError(groupRes.error);
      return;
    }

    const memberIds = members.map((m) => m.id);
    let renamed = 0;

    if (retitleMembers && memberIds.length > 0) {
      if (canEditCode && codePreview && codePreview.matchedCount > 0) {
        const r = await bulkUpdateManufacturingRecipesMeta({
          recipe_ids: memberIds,
          rename: {
            field: "code",
            find: codeFind,
            replace: nextCode,
            case_insensitive: true,
          },
        });
        if (!r.ok) {
          setBusy(false);
          onError(`Group updated, but member codes failed: ${r.error}`);
          onDone(`Renamed series to ${nextName} (${nextCode})`);
          return;
        }
        renamed += r.data.updated_count;
      }

      if (namePreview && namePreview.matchedCount > 0) {
        const r = await bulkUpdateManufacturingRecipesMeta({
          recipe_ids: memberIds,
          rename: {
            field: "name",
            find: nameFind,
            replace: nextName,
            case_insensitive: true,
          },
        });
        if (!r.ok) {
          setBusy(false);
          onError(`Group updated, but member names failed: ${r.error}`);
          onDone(`Renamed series to ${nextName} (${nextCode})`);
          return;
        }
        renamed += r.data.updated_count;
      }
    }

    setBusy(false);
    setOpen(false);
    const parts = [`Renamed series to ${nextName} (${nextCode})`];
    if (renamed > 0) parts.push(`${renamed} member update${renamed === 1 ? "" : "s"}`);
    onDone(parts.join(" · "));
  }

  if (!open) {
    return (
      <button
        type="button"
        className={ghostBtnClass}
        disabled={busy}
        onClick={() => {
          resetFromGroup();
          setOpen(true);
        }}
      >
        Rename series
      </button>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
          Rename series
        </div>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={busy}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>

      <div className="space-y-2">
        <div className="text-center text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Change To
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-slate-600 dark:text-slate-300">Series name</span>
            <input
              className={`${inputClass} mt-1`}
              value={name}
              onChange={(e) => {
                const next = e.target.value;
                setName(next);
                const prevSuggested = seriesCodeFromName(group.name);
                if (!code.trim() || code === group.code || code === prevSuggested) {
                  const suggested = seriesCodeFromName(next);
                  if (suggested) setCode(suggested);
                }
              }}
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600 dark:text-slate-300">Series code</span>
            <input
              className={`${inputClass} mt-1`}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
        </div>
      </div>

      <label className="inline-flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
        <input
          type="checkbox"
          checked={retitleMembers}
          onChange={(e) => setRetitleMembers(e.target.checked)}
        />
        Also retitle member products ({members.length})
      </label>

      {retitleMembers ? (
        <div className="space-y-2">
          <div className="text-center text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Change From
          </div>
          {!canEditCode ? (
            <p className="text-[11px] text-amber-800 dark:text-amber-200">
              Catalog code edits need org admin — member codes will stay as-is; names can still
              update.
            </p>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block text-xs text-slate-600 dark:text-slate-400">
              Find in names
              <input
                className={`${inputClass} mt-1`}
                value={nameFind}
                onChange={(e) => setNameFind(e.target.value)}
                placeholder="e.g. Series"
              />
            </label>
            {canEditCode ? (
              <label className="block text-xs text-slate-600 dark:text-slate-400">
                Find in codes
                <input
                  className={`${inputClass} mt-1`}
                  value={codeFind}
                  onChange={(e) => setCodeFind(e.target.value)}
                  placeholder="e.g. SERIES"
                />
              </label>
            ) : (
              <div />
            )}
          </div>
        </div>
      ) : null}

      {retitleMembers ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {matchedCount > 0
            ? `${matchedCount} member field match${matchedCount === 1 ? "" : "es"} ready`
            : "No member name/code matches for the find strings yet."}
          {blockers.length > 0 ? ` · ${blockers.length} blocker(s)` : ""}
        </p>
      ) : null}

      <button
        type="button"
        className={mfgPrimaryBtnClass}
        disabled={busy || blockers.length > 0 || !nextName || !nextCode}
        onClick={() => void applyRename()}
      >
        {busy ? "Saving…" : "Save rename"}
      </button>
    </div>
  );
}
